import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import { agents } from '../../../lib/ai/agentCaller';
import { supabase } from '../../../lib/supabase';
import { 
  MagnifyingGlassIcon,
  DocumentTextIcon, 
  TableCellsIcon, 
  GlobeAltIcon,
  XMarkIcon 
} from '@heroicons/react/24/outline';
import { useProject } from '../../../context/ProjectContext';

export default function GenerateWithCliveModal({ isOpen, onClose, onGenerate }) {
  const { currentProject } = useProject();
  const [settings, setSettings] = useState({
    agent: 'dave',
    includeProfile: 'no',
    prompt: '',
    wordCount: 1000
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && searchQuery) {
      searchDocuments();
    }
  }, [searchQuery, isOpen]);

  const searchDocuments = async () => {
    setLoading(true);
    try {
      // Search in data_docs
      const { data: docData, error: docError } = await supabase
        .from('data_docs')
        .select('id, name, content, created_at')
        .eq('project_id', currentProject.id)
        .ilike('name', `%${searchQuery}%`)
        .limit(5);

      // Search in data_tables
      const { data: tableData, error: tableError } = await supabase
        .from('data_tables')
        .select('id, name, dataset, created_at')
        .eq('project_id', currentProject.id)
        .ilike('name', `%${searchQuery}%`)
        .limit(5);

      // Search in website_seo_checks
      const { data: seoData, error: seoError } = await supabase
        .from('website_seo_checks')
        .select('id, title, url, keyword, seo_results, created_at')
        .eq('project_id', currentProject.id)
        .ilike('title', `%${searchQuery}%`)
        .limit(5);

      if (docError || tableError || seoError) throw docError || tableError || seoError;

      const formattedDocs = [
        ...(docData || []).map(doc => ({
          ...doc,
          sourceType: 'document'
        })),
        ...(tableData || []).map(table => ({
          ...table,
          name: table.name,
          sourceType: 'table',
          content: JSON.stringify(table.dataset) // Stringify dataset for tables
        })),
        ...(seoData || []).map(seo => ({
          ...seo,
          name: seo.title, // Use title as name for consistency
          sourceType: 'seo',
          content: JSON.stringify({url: seo.url, keyword: seo.keyword, test_results: seo.seo_results}) // Stringify SEO results
        }))
      ];

      setDocuments(formattedDocs);
    } catch (error) {
      console.error('Error searching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = (doc) => {
    if (!selectedDocs.find(d => d.id === doc.id)) {
      setSelectedDocs([...selectedDocs, doc]);
    }
  };

  const handleRemoveDocument = (docId) => {
    setSelectedDocs(selectedDocs.filter(d => d.id !== docId));
  };

  const handleGenerate = () => {
    onGenerate({
      ...settings,
      referenceDocs: selectedDocs.map(doc => ({
        id: doc.id,
        name: doc.name,
        content: doc.content,
        type: doc.sourceType
      }))
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Generate with Clive" size="xlarge">
      <div className="flex gap-6">
        {/* Left section - Document picker */}
        <div className="w-1/3 border-r border-divider pr-6">
          <h3 className="text-lg font-semibold mb-4">Reference Documents</h3>
          
          {/* Search input */}
          <div className="relative mb-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search documents..."
              className="w-full bg-dark-300 rounded-lg px-4 py-2 pl-10 focus:outline-none focus:ring-1 focus:ring-accent-primary"
            />
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
          </div>

          {/* Search results */}
          <div className="mb-6">
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent-primary"></div>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <button
                    key={`${doc.sourceType}-${doc.id}`}
                    onClick={() => handleAddDocument(doc)}
                    className="w-full text-left p-2 hover:bg-dark-300 rounded-lg flex items-center gap-2"
                  >
                    {doc.sourceType === 'document' ? (
                      <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                    ) : doc.sourceType === 'table' ? (
                      <TableCellsIcon className="w-5 h-5 text-gray-400" />
                    ) : (
                      <GlobeAltIcon className="w-5 h-5 text-gray-400" />
                    )}
                    <span>{doc.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected documents */}
          <div>
            <h4 className="text-sm font-medium text-gray-400 mb-2">Selected Documents</h4>
            <div className="space-y-2">
              {selectedDocs.map((doc) => (
                <div
                  key={`selected-${doc.sourceType}-${doc.id}`}
                  className="flex items-center justify-between p-2 bg-dark-300 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    {doc.sourceType === 'document' ? (
                      <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                    ) : doc.sourceType === 'table' ? (
                      <TableCellsIcon className="w-5 h-5 text-gray-400" />
                    ) : (
                      <GlobeAltIcon className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="truncate">{doc.name}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveDocument(doc.id)}
                    className="text-gray-400 hover:text-white"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right section - Original settings */}
        <div className="flex-1">
          {/* Agent Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Agent</label>
            <select
              value={settings.agent}
              onChange={(e) => setSettings(prev => ({ ...prev, agent: e.target.value }))}
              className="w-full px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary"
            >
              {Object.entries(agents).map(([key, agent]) => (
                <option key={key} value={key}>{agent.name} ({agent.description})</option>
              ))}
            </select>
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium mb-2">Instructions</label>
            <textarea
              value={settings.prompt}
              onChange={(e) => setSettings(prev => ({ ...prev, prompt: e.target.value }))}
              className="w-full h-32 px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary resize-none"
              placeholder="What would you like Clive to do?"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-divider">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleGenerate}
          className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90"
        >
          Generate
        </button>
      </div>
    </Modal>
  );
}